import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  CalendarClock,
  ChevronRight,
  FileText,
  MoreVertical,
  Pencil,
  SendHorizontal,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useLocale } from "@/hooks/useLocale";
import {
  CHAT_MAX_TEXT_LENGTH,
  type IChatScheduledMessage,
} from "@/types/(my-space)/chat.interface";
import type { ScheduledMessagesState } from "../_hooks/useScheduledMessages";
import { messageContentPreview, scheduledTimeLabel } from "../_lib/chat-labels";
import { ScheduleDialog } from "./ScheduleDialog";

// ── Bar above the composer ──────────────────────────────────────────────────

export const ScheduledBar = ({
  count,
  failedCount,
  onOpen,
}: {
  count: number;
  failedCount: number;
  onOpen: () => void;
}) => {
  const { t } = useTranslation("chat");
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      className="h-9 w-full justify-start gap-2 rounded-none border-t border-border bg-background px-4 font-normal md:px-5"
    >
      <CalendarClock className="size-4 shrink-0 text-primary" />
      <span className="truncate text-xs font-medium text-foreground">
        {t("scheduled.bar_count", { count })}
      </span>
      {failedCount ? (
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-danger">
          <AlertCircle className="size-3.5" />
          {t("scheduled.bar_failed", { count: failedCount })}
        </span>
      ) : null}
      <ChevronRight className="ms-auto size-4 shrink-0 text-muted-foreground rtl-flip" />
    </Button>
  );
};

// ── The list ────────────────────────────────────────────────────────────────

interface ScheduledDrawerProps {
  open: boolean;
  onClose: () => void;
  scheduled: ScheduledMessagesState;
}

export const ScheduledMessagesDrawer = ({ open, onClose, scheduled }: ScheduledDrawerProps) => {
  const { t } = useTranslation("chat");
  const { items, isLoading } = scheduled;
  const [rescheduling, setRescheduling] = useState<{
    item: IChatScheduledMessage;
    at: Date;
  } | null>(null);
  const [toDelete, setToDelete] = useState<IChatScheduledMessage | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  // The last one sent or removed: nothing left to show.
  useEffect(() => {
    if (open && !isLoading && items.length === 0) onClose();
  }, [open, isLoading, items.length, onClose]);

  useEffect(() => {
    if (!open) setEditingId(null);
  }, [open]);

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={onClose}
        title={t("scheduled.title")}
        size="md"
        isDismissable={!rescheduling && !toDelete}
      >
        <div className="flex flex-col gap-2 pb-6">
          <p className="text-xs text-muted-foreground">{t("scheduled.list_hint")}</p>
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <ScheduledRow
                key={item.id}
                item={item}
                busy={scheduled.busyId === item.id}
                isEditing={editingId === item.id}
                onSendNow={() => void scheduled.sendNow(item)}
                onReschedule={() => setRescheduling({ item, at: new Date(item.sendAt) })}
                onStartEdit={() => setEditingId(item.id)}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={async (body) => {
                  if (await scheduled.editText(item, body)) setEditingId(null);
                }}
                onDelete={() => setToDelete(item)}
              />
            ))}
          </ul>
        </div>
      </Drawer>

      <ScheduleDialog
        open={!!rescheduling}
        initial={rescheduling?.at ?? null}
        isSubmitting={!!rescheduling && scheduled.busyId === rescheduling.item.id}
        onClose={() => setRescheduling(null)}
        onConfirm={async (at) => {
          if (!rescheduling) return;
          if (await scheduled.reschedule(rescheduling.item, at)) setRescheduling(null);
        }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("scheduled.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("scheduled.delete_message")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("composer.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-danger text-danger-foreground hover:bg-danger/90"
              onClick={() => {
                const target = toDelete;
                setToDelete(null);
                if (target) void scheduled.cancel(target);
              }}
            >
              {t("actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ── One row ─────────────────────────────────────────────────────────────────

interface ScheduledRowProps {
  item: IChatScheduledMessage;
  busy: boolean;
  isEditing: boolean;
  onSendNow: () => void;
  onReschedule: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (body: string) => Promise<void>;
  onDelete: () => void;
}

const ScheduledRow = ({
  item,
  busy,
  isEditing,
  onSendNow,
  onReschedule,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: ScheduledRowProps) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const [draft, setDraft] = useState(item.body ?? "");
  const failed = item.status === "failed";
  const sending = item.status === "sending";
  const isText = item.type === "text";
  // Text, or a caption on media/files — the only part that can be edited.
  const canEditText = isText || !!item.body?.trim();
  const images = item.attachments.filter((a) => a.kind === "image");
  const others = item.attachments.filter((a) => a.kind !== "image");
  const trimmed = draft.trim();
  const canSave = (isText ? !!trimmed : true) && trimmed !== (item.body ?? "").trim();

  useEffect(() => {
    if (isEditing) setDraft(item.body ?? "");
  }, [isEditing, item.body]);

  return (
    <li className="flex items-start gap-3 rounded-md border border-border bg-surface p-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-xs bg-primary-muted text-primary">
        <CalendarClock className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {scheduledTimeLabel(t, formatDate, item.sendAt)}
          </span>
          {failed || sending ? (
            <Badge variant={statusToVariant(item.status)}>
              {failed ? t("scheduled.status_failed") : t("scheduled.status_sending")}
            </Badge>
          ) : null}
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={CHAT_MAX_TEXT_LENGTH}
              aria-label={isText ? t("scheduled.edit_text") : t("scheduled.edit_caption")}
              className="max-h-40 text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onCancelEdit} disabled={busy}>
                {t("composer.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void onSaveEdit(trimmed)}
                disabled={!canSave}
                isLoading={busy}
              >
                {t("scheduled.save")}
              </Button>
            </div>
          </div>
        ) : item.body?.trim() ? (
          <p
            dir="auto"
            className="line-clamp-3 text-start text-sm break-words whitespace-pre-wrap text-foreground"
          >
            {item.body}
          </p>
        ) : !item.attachments.length ? (
          <p className="text-sm text-muted-foreground">
            {messageContentPreview(t, { type: item.type, body: item.body })}
          </p>
        ) : null}

        {images.length ? (
          <div className="flex flex-wrap gap-1.5">
            {images.map((a) => (
              <img
                key={a.fileId}
                src={a.fileUrl}
                alt={a.fileName}
                title={a.fileName}
                className="size-12 rounded-xs border border-border object-cover"
              />
            ))}
          </div>
        ) : null}
        {others.length ? (
          <div className="flex flex-wrap gap-1.5">
            {others.map((a) => (
              <span
                key={a.fileId}
                className="flex max-w-full items-center gap-1.5 rounded-xs border border-border bg-muted/40 px-2 py-1 text-xs text-foreground"
              >
                <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                <span dir="auto" className="truncate">
                  {a.fileName}
                </span>
              </span>
            ))}
          </div>
        ) : null}

        {failed ? (
          <p className="flex items-start gap-1.5 text-xs text-danger">
            <AlertCircle className="mt-px size-3.5 shrink-0" />
            {item.failureMessage || t("scheduled.failed_generic")}
          </p>
        ) : null}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={busy || sending}
            aria-label={t("scheduled.actions")}
            className="shrink-0"
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onSendNow}>
            <SendHorizontal className="size-4 rtl-flip" />
            {t("scheduled.send_now")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onReschedule}>
            <CalendarClock className="size-4" />
            {t("scheduled.reschedule")}
          </DropdownMenuItem>
          {canEditText ? (
            <DropdownMenuItem onSelect={onStartEdit}>
              <Pencil className="size-4" />
              {isText ? t("scheduled.edit_text") : t("scheduled.edit_caption")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-danger focus:text-danger" onSelect={onDelete}>
            <Trash2 className="size-4" />
            {t("actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
};
